import type { User } from "firebase/auth";
import { ArrowDown, ArrowRight, LoaderCircle, LogIn, UsersRound } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { GameMenu } from "./GameMenu";
import { usePlayerProfile } from "../hooks/usePlayerProfile";
import { usePremiumStatus } from "../hooks/usePremiumStatus";
import { isPremiumUser } from "../lib/premium";
import { AvatarArtwork } from "./AvatarPicker";
import { quests } from "../data/quests";
import { Logo } from "./Logo";
import { QuestIcon } from "./QuestIcon";
import { HomePartyScene } from "./HomePartyScene";
import { SiteFooter } from "./SiteFooter";
import "./HomePage.css";

export function SiteTopbar({ user, profileAvatar }: { user?: User | null; profileAvatar?: string | null }) {
  const signedIn = Boolean(user && !user.isAnonymous);
  const saved = usePlayerProfile(signedIn && profileAvatar === undefined ? user : null);
  const { status: billing } = usePremiumStatus(signedIn ? user : null);
  const avatar = profileAvatar === undefined ? saved.avatar : profileAvatar;
  const premium = isPremiumUser(user) || Boolean(billing?.active && billing.accessUntil && billing.accessUntil > Date.now());
  return (
    <nav className="topbar" aria-label="Primary navigation">
      <Logo />
      <div className="nav-actions">
        <GameMenu hostMode={signedIn} />
        {user === undefined ? <span className="account-link" role="status"><LoaderCircle className="spin" size={16} aria-hidden="true" /> Checking sign-in…</span> : <Link className="account-link" to={signedIn ? "/account" : "/login"}>{signedIn ? <><AvatarArtwork avatar={avatar} premium={premium} /> My profile</> : <><LogIn size={16} /> Log in</>}</Link>}
        <Link className="host-link" to="/host">Host a game <ArrowRight size={16} /></Link>
      </div>
    </nav>
  );
}

export function JourneyShell({ user, profileAvatar, children }: { user?: User | null; profileAvatar?: string | null; children: ReactNode }) {
  const location = useLocation();
  const gameHref = (id: string) => {
    const params = new URLSearchParams(location.pathname === "/" ? location.search : "");
    params.set("quest", id);
    return `/?${params.toString()}`;
  };

  return (
    <main className="hub-home journey-home">
      <div className="site-shell hub-shell">
        <SiteTopbar user={user} profileAvatar={profileAvatar} />
        <section className="hub-hero journey-layout">
          {children}
          <div className="hub-intro journey-intro">
            <h1 className="home-title" aria-label="SideQuest Headquarter">
              {["SideQuest", "Headquarter"].map((word, wordIndex) => <span className="home-title-word" aria-hidden="true" key={word}>
                {[...word].map((letter, index) => <span className="home-title-letter" key={index} style={{ "--letter-index": index + wordIndex * 9 } as CSSProperties}>{letter}</span>)}
                {wordIndex === 0 && " "}
              </span>)}
            </h1>
            <p>Big guesses. Terrible bluffs. Surprisingly good company.<br className="home-copy-break" /> Take a little break and get everyone in on the fun.</p>
            <a className="journey-explore" href="#games">Find your next game <ArrowDown size={16} /></a>
            <HomePartyScene />
          </div>
        </section>
        <section className="home-game-library" id="games" aria-labelledby="game-library-title">
          <header><h2 id="game-library-title">What are we playing?</h2></header>
          <div className="home-game-grid">
            {quests.map(({ id, title, description, players, iconSrc, tone }, index) => <Link className={`home-game-card home-game-${tone}`} to={gameHref(id)} key={id}>
              <div className="home-game-card-top"><span className="home-game-icon"><QuestIcon src={iconSrc} /></span><span className="home-game-number">0{index + 1}</span></div>
              <h3>{title}</h3><p>{description}</p><div className="home-game-card-footer"><span><UsersRound size={14} /> {players}</span><ArrowRight size={18} /></div>
            </Link>)}
          </div>
        </section>
        <SiteFooter />
      </div>
    </main>
  );
}
